import {useCallback, useEffect, useMemo, useState} from 'react'
import {useRouter, useSearchParams} from '@/lib/router'

import type {Account} from '@/types'
import {avatarDisplaySrc, saveStoredAccount} from '@/lib/utils'

import {ToastProvider} from '@/components/Toast'
import {PrimaryRail} from '@/components/shell/PrimaryRail'
import {SecondaryColumn} from '@/components/shell/SecondaryColumn'
import {MainPane} from '@/components/shell/MainPane'
import {MobileBottomNav} from '@/components/shell/MobileBottomNav'
import {MobileSubNav} from '@/components/shell/MobileSubNav'
import {ChatList} from '@/components/conversation/ChatList'
import {ViewRouter} from '@/components/shell/ViewRouter'
import {apiGet, apiPost, SESSION_EXPIRED_EVENT, getAuthToken, AUTH_EXPIRED_EVENT, clearAuthToken} from '@/lib/api'
import {LoginPage} from '@/views/login/LoginPage'
import {ConfirmDialog} from '@/components/ConfirmDialog'

import {useAccountProfile} from '@/hooks/useAccountProfile'
import {useLogin} from '@/hooks/useLogin'
import {useContactsSync} from '@/hooks/useContactsSync'
import {useBotData} from '@/hooks/useBotData'
import {useRouteContext} from '@/hooks/useRouteContext'
import {useLabels} from '@/hooks/useLabels'
import {readUnreadTotal, useMessageNotifications} from '@/hooks/useMessageNotifications'
import {useAccounts} from '@/hooks/useAccounts'

export default function App() {
    const [authed, setAuthed] = useState(() => !!getAuthToken())

    useEffect(() => {
        const onAuthExpired = () => setAuthed(false)
        window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired)
        return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired)
    }, [])

    if (!authed) {
        return (
            <ToastProvider>
                <LoginPage onLogin={() => setAuthed(true)}/>
            </ToastProvider>
        )
    }

    return (
        <ToastProvider>
            <AuthenticatedApp onLogout={() => setAuthed(false)}/>
        </ToastProvider>
    )
}

function AuthenticatedApp({onLogout}: {onLogout: () => void}) {
    const router = useRouter()
    const searchParams = useSearchParams()
    const [account, setAccount] = useState<Account | null>(null)
    const [currentInstanceWxid, setCurrentInstanceWxid] = useState('')
    const [currentInstanceLoaded, setCurrentInstanceLoaded] = useState(false)
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)

    // Load current instance from server on mount
    useEffect(() => {
        apiGet<{success: boolean; wxid: string}>('/current-instance', {silent: true})
            .then((res) => {
                setCurrentInstanceWxid(res.wxid || '')
            })
            .catch(() => setCurrentInstanceWxid(''))
            .finally(() => setCurrentInstanceLoaded(true))
    }, [])

    const {accountProfile, profileRefreshing, refreshProfile, reset: resetAccountProfile} =
        useAccountProfile(account, setAccount)
    const {
        qrImage,
        loginStatus,
        loginLoading,
        loginSuccessVersion,
        refreshQr,
    } = useLogin(account, setAccount, {
        onProfileRefresh: refreshProfile,
    })
    const {contacts, syncing, syncContacts, resolveContact, updateContact, reset: resetContacts} = useContactsSync(account)
    const {rules, tasks, settings, saveRule, deleteRule, saveTask, deleteTask, refreshTasks, saveSettings} =
        useBotData(account)
    const {activeMenu, activeItem, activeConversationWxid, groups, friends} = useRouteContext(contacts)
    const activeConversationAccountWxid = searchParams.get('accountWxid') || account?.wxid || ''
    const {labels} = useLabels(account)
    const openConversation = useCallback((wxid: string, accountWxid?: string) => {
        const params = new URLSearchParams({conversationWxid: wxid})
        if (accountWxid) params.set('accountWxid', accountWxid)
        router.push(`/conversation/realtime?${params.toString()}`)
    }, [router])
    const {unreadCounts, totalUnread, markConversationRead, markAllConversationsRead} = useMessageNotifications({
        account,
        contacts,
        messageNotifications: settings.system.messageNotifications,
        activeConversationWxid,
        conversationPageActive: activeMenu === 'conversation' && activeItem.id === 'realtime',
        onOpenConversation: openConversation,
    })

    const isLoggedIn = !!account

    const {accounts, loading: accountsLoading} = useAccounts(account?.wxid)

    useEffect(() => {
        if (!currentInstanceLoaded || accountsLoading) return
        if (account) {
            const found = accounts.find((item) => item.wxid === account.wxid)
            if ((accounts.length && !found) || (found && found.status !== 'online')) {
                clearCurrentInstance()
            }
            return
        }
        if (!currentInstanceWxid) return
        const found = accounts.find((item) => item.wxid === currentInstanceWxid)
        if (found?.status === 'online') {
            const next = {wxid: found.wxid, nickName: found.nickName, avatar: found.avatar}
            saveStoredAccount(next)
            setAccount(next)
            return
        }
        setCurrentInstanceWxid('')
        saveStoredAccount(null)
        apiPost('/current-instance', {wxid: ''}, {silent: true}).catch(() => {})
        router.push('/settings/personal')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentInstanceLoaded, accountsLoading, accounts, account?.wxid, currentInstanceWxid])

    // Enrich account with full info from accounts list when it loads
    useEffect(() => {
        if (!account || !accounts.length) return
        const found = accounts.find((a) => a.wxid === account.wxid)
        if (found && (!account.avatar || account.nickName === account.wxid)) {
            setAccount({wxid: found.wxid, nickName: found.nickName, avatar: found.avatar})
        }
    }, [accounts, account?.wxid])
    const accountUnreadCounts = useMemo(() => {
        const counts: Record<string, number> = {}
        for (const item of accounts) {
            counts[item.wxid] = item.wxid === account?.wxid ? totalUnread : readUnreadTotal(item.wxid)
        }
        return counts
    }, [account?.wxid, accounts, totalUnread])

    function switchAccount(wxid: string) {
        const target = accounts.find((a) => a.wxid === wxid)
        if (!target) return
        if (target.status !== 'online') {
            router.push('/settings/personal')
            return
        }
        const next: Account = {
            wxid: target.wxid,
            nickName: target.nickName,
            avatar: target.avatar,
        }
        saveStoredAccount(next)
        apiPost('/current-instance', {wxid: target.wxid}, {silent: true}).catch(() => {})
        setCurrentInstanceWxid(target.wxid)
        setAccount(next)
        resetAccountProfile()
        resetContacts()
    }

    function handleConversationSelect(wxid: string, accountWxid?: string) {
        openConversation(wxid, accountWxid || account?.wxid)
    }

    function clearCurrentInstance() {
        saveStoredAccount(null)
        setAccount(null)
        setCurrentInstanceWxid('')
        resetAccountProfile()
        resetContacts()
        apiPost('/current-instance', {wxid: ''}, {silent: true}).catch(() => {})
        router.push('/settings/personal')
    }

    async function logout() {
        const wxid = account?.wxid
        try {
            if (wxid) {
                await apiPost('/login/logout', {wxid})
            }
        } catch {
            // requestJson already shows the upstream error
        } finally {
            clearCurrentInstance()
        }
    }

    async function confirmSystemLogout() {
        setLogoutConfirmOpen(false)
        try {
            await apiPost('/current-instance', {wxid: ''}, {silent: true})
        } catch {
            // Leaving the app should not be blocked by current-instance cleanup.
        } finally {
            saveStoredAccount(null)
            clearAuthToken()
            onLogout()
        }
    }

    useEffect(() => {
        const onSessionExpired = () => {
            if (!account) return
            clearCurrentInstance()
        }
        window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired)
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account])

    return (
        <div className="flex h-full min-h-0 w-full overflow-hidden overscroll-none font-sans">
            <PrimaryRail
                avatarUrl={avatarDisplaySrc(account?.avatar, account?.wxid || 'bot')}
                activeMenu={activeMenu}
                isLoggedIn={isLoggedIn}
                conversationUnreadCount={totalUnread}
                accountUnreadCounts={accountUnreadCounts}
                accounts={accounts}
                currentWxid={account?.wxid}
                onNavigate={(path) => router.push(path)}
                onSwitchAccount={switchAccount}
                onRefreshProfile={() => refreshProfile()}
                onManageAccounts={() => router.push('/settings/personal')}
            />
            {activeMenu !== 'plugin' ? (
                <SecondaryColumn
                    activeMenu={activeMenu}
                    activeItemId={activeItem.id}
                    isLoggedIn={isLoggedIn}
                    onNavigate={(path) => router.push(path)}
                    onLogout={() => setLogoutConfirmOpen(true)}
                >
                    {activeMenu === 'conversation' && account ? (
                        <ChatList
                            contacts={contacts}
                            account={account}
                            accounts={accounts}
                            layout={settings.system.conversationLayout || 'single'}
                            activeAccountWxid={activeConversationAccountWxid}
                            activeConversationWxid={activeConversationWxid}
                            unreadCounts={unreadCounts}
                            accountUnreadCounts={accountUnreadCounts}
                            onConversationRead={markConversationRead}
                            onAccountReadAll={markAllConversationsRead}
                            onSelect={handleConversationSelect}
                        />
                    ) : null}
                </SecondaryColumn>
            ) : null}
            {activeMenu === 'conversation' && !activeConversationWxid ? (
                <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-page pb-14 md:hidden">
                    <header className="flex h-[60px] shrink-0 items-center border-b border-hair bg-page px-4">
                        <h1 className="text-base font-medium text-ink">聊天</h1>
                    </header>
                    {account ? <ChatList
                        contacts={contacts} account={account} accounts={accounts}
                        layout={settings.system.conversationLayout || 'single'}
                        activeAccountWxid={activeConversationAccountWxid}
                        activeConversationWxid={activeConversationWxid}
                        unreadCounts={unreadCounts} accountUnreadCounts={accountUnreadCounts}
                        onConversationRead={markConversationRead} onAccountReadAll={markAllConversationsRead}
                        onSelect={handleConversationSelect}
                    /> : null}
            </main>
            ) : null}
            <div className={activeMenu === 'conversation' && !activeConversationWxid ? 'hidden md:flex min-h-0 min-w-0 flex-1' : 'flex min-h-0 min-w-0 flex-1'}>
            <MobileSubNav activeMenu={activeMenu} activeItemId={activeItem.id} isLoggedIn={isLoggedIn} onNavigate={(path) => router.push(path)}/>
            <MainPane>
                <ViewRouter
                    activeItemId={activeItem.id}
                    account={account}
                    accounts={accounts}
                    contacts={contacts}
                    friends={friends}
                    groups={groups}
                    labels={labels}
                    rules={rules}
                    tasks={tasks}
                    settings={settings}
                    accountProfile={accountProfile}
                    syncing={syncing}
                    profileRefreshing={profileRefreshing}
                    qrImage={qrImage}
                    loginStatus={loginStatus}
                    loginLoading={loginLoading}
                    loginSuccessVersion={loginSuccessVersion}
                    onRefreshQr={refreshQr}
                    saveRule={saveRule}
                    deleteRule={deleteRule}
                    saveTask={saveTask}
                    deleteTask={deleteTask}
                    refreshTasks={refreshTasks}
                    saveSettings={saveSettings}
                    syncContacts={syncContacts}
                    resolveContact={resolveContact}
                    updateContact={updateContact}
                    refreshProfile={() => refreshProfile()}
                    logout={logout}
                    systemLogout={() => setLogoutConfirmOpen(true)}
                />
            </MainPane>
            </div>
            <MobileBottomNav activeMenu={activeMenu} onNavigate={(path) => router.push(path)}/>
            <ConfirmDialog
                open={logoutConfirmOpen}
                title="确认退出登录？"
                description="退出后需要重新登录才能继续使用。"
                confirmLabel="退出登录"
                danger
                onConfirm={confirmSystemLogout}
                onCancel={() => setLogoutConfirmOpen(false)}
            />
        </div>
    )
}
